import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { getShared, fetchSharedBytes } from '../services/api';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const ZOOM = 1.5;

type Annotation = {
  anno_id: string;
  page_index: number;
  boxes: { x: number; y: number; width: number; height: number }[];
  text: string;
  comment: string;
  color: string;
  author_name?: string;
  replies?: { reply_id: string; text: string; author_name?: string }[];
};

export default function SharedAnnotatorPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [docMeta, setDocMeta] = useState<any | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareToken) return;
    let cancelled = false;
    (async () => {
      try {
        const [meta, bytes] = await Promise.all([
          getShared(shareToken),
          fetchSharedBytes(shareToken),
        ]);
        if (cancelled) return;
        setDocMeta(meta.document);
        setAnnotations(meta.annotations || []);
        const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
        if (!cancelled) setPdf(pdfDoc);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading) return <div className="p-12 text-center text-gray-400">Loading shared document…</div>;
  if (error) return <div className="p-12 text-center text-red-500">{error}</div>;
  if (!pdf) return <div className="p-12 text-center text-gray-400">Could not open this document.</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1400px] mx-auto flex gap-4">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400">Shared (read-only)</div>
            <h1 className="text-lg font-semibold text-gray-800 truncate">{docMeta?.label || 'Document'}</h1>
            <p className="text-xs text-gray-400 mt-1">{annotations.length} annotation{annotations.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="space-y-4">
            {Array.from({ length: pdf.numPages }, (_, i) => (
              <ReadOnlyPage key={i} pdf={pdf} pageIndex={i}
                annotations={annotations.filter((a) => a.page_index === i)} />
            ))}
          </div>
        </div>

        <aside className="w-80 flex-shrink-0">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sticky top-6">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
              Annotations ({annotations.length})
            </div>
            {annotations.length === 0 ? (
              <p className="text-xs text-gray-400">No annotations on this document.</p>
            ) : (
              <ul className="space-y-3">
                {annotations.map((a) => (
                  <li key={a.anno_id} className="text-xs border-l-2 pl-3 py-1"
                    style={{ borderColor: a.color }}>
                    <div className="text-gray-400">
                      p.{a.page_index + 1}
                      {a.author_name && <span className="ml-1">· {a.author_name}</span>}
                    </div>
                    <div className="italic text-gray-700 line-clamp-2">"{a.text}"</div>
                    {a.comment && <div className="text-gray-600 mt-1">{a.comment}</div>}
                    {a.replies && a.replies.length > 0 && (
                      <ul className="mt-2 ml-2 space-y-1 border-l border-gray-200 pl-3">
                        {a.replies.map((r) => (
                          <li key={r.reply_id}>
                            <div className="text-[10px] text-gray-400">{r.author_name || 'Anonymous'}</div>
                            <div className="text-gray-700">{r.text}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function ReadOnlyPage({ pdf, pageIndex, annotations }: { pdf: PDFDocumentProxy; pageIndex: number; annotations: Annotation[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page: PDFPageProxy = await pdf.getPage(pageIndex + 1);
      if (cancelled || !canvasRef.current) return;
      const viewport = page.getViewport({ scale: ZOOM });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
      if (!cancelled) setSize({ w: viewport.width, h: viewport.height });
    })();
    return () => { cancelled = true; };
  }, [pdf, pageIndex]);

  return (
    <div className="relative inline-block bg-white shadow border border-gray-200">
      <canvas ref={canvasRef} className="block" />
      {size && (
        <div className="absolute inset-0 pointer-events-none" style={{ width: size.w, height: size.h }}>
          {annotations.map((a) => a.boxes.map((b, i) => (
            <div key={`${a.anno_id}-${i}`} className="absolute"
              style={{
                left: b.x * ZOOM, top: b.y * ZOOM,
                width: b.width * ZOOM, height: b.height * ZOOM,
                backgroundColor: a.color, opacity: 0.4,
                border: `1px solid ${a.color}`,
              }}
              title={a.comment} />
          )))}
        </div>
      )}
    </div>
  );
}
